import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Box, Stack } from '@mui/material';
import { StyledFilterWrapper } from 'molecules/Filter/Filter.styles';
import { StyledPaper } from 'pages/ContactsPage/Contacts.styles';
import axios, { AxiosResponse } from 'axios';
import { IFilterOptionItem } from 'organisms/Filter/types';
import { initialValue } from 'organisms/SalesReportTable/GoogleSalesReportTable';
import { IDealInvoiceItem } from 'organisms/DealInvoiceChart/types';
import { InvoiceDetails } from 'organisms/DealInvoiceChart/components/InvoiceDetails';
import { SelectWithCheckbox } from 'molecules/Select/SelectWithCheckbox';
import ButtonDateRangePicker from 'molecules/Calendar/ButtonDateRangePicker';
import DealInvoiceChart2 from 'organisms/DealInvoiceChart/index2';
import {
  ServiceType,
  transformAndGroupDataByMonth,
} from 'organisms/DealInvoiceChart/helpers';
import dayjs from 'dayjs';
import { SALES_REP } from 'utils/constants/roles';
import { useBaoSelector } from 'utils/hooks/redux';
import SalesReportTable, { SalesReportRow } from 'organisms/SalesReportTable';
import InvoicePreview from 'molecules/InvoicePreview';

type FilterOptions = {
  company: { id: string; name: string }[];
  sales_rep: { id: string; name: string }[];
  accountant: { id: string; name: string }[];
  google_sales_rep: { id: string; name: string }[];
  googler_names: string[];
  service_type: { id: string; name: string }[];
  invoice_status: { id: string; name: string }[];
  deal_stage: { id: string; name: string }[];
};

export type RevenuePipelineItem = {
  legend_key: ServiceType;
  legend_name: string;
  items: { year: string; amount: number }[];
};

interface IDataState {
  count: number;
  items: IDealInvoiceItem[];
  currencies: string[];
  // invoices: GoogleSalesReportRow[];
  invoice_total: number;
  paid_invoice_total: number;
  revenue_pipeline: RevenuePipelineItem[];

  deals: SalesReportRow[];
  // count: 0
  // currencies: string[];
  // items: [],
}

export interface ExtendedOptionItem extends IFilterOptionItem {
  currency: string;
}

const initialState = {
  count: 0,
  deals: [],
  items: [],
  invoice_total: 0,
  paid_invoice_total: 0,
  currencies: [],
  revenue_pipeline: [
    {
      legend_key: 'gcp_rev' as ServiceType,
      legend_name: 'GCP Revenue',
      items: [],
    },
    {
      legend_key: 'psfdaf_rev' as ServiceType,
      legend_name: 'PSF/DAF Services Revenue',
      items: [],
    },
    {
      legend_key: 'gcp_pipeline' as ServiceType,
      legend_name: 'GCP Pipeline',
      items: [],
    },
    {
      legend_key: 'psfdaf_pipeline' as ServiceType,
      legend_name: 'PSF/DAF Services Pipeline',
      items: [],
    },
  ],
};

const SalesReport = () => {
  const user = useBaoSelector((state) => state.user.value);

  const [loading, setLoading] = useState<boolean>(false);
  const [filtersLoading, setFiltersLoading] = useState<boolean>(false);
  const [data, setData] = useState<IDataState>(initialState);
  const [options, setOptions] = useState<{
    companies: { id: string; name: string }[];
    invoice_status: { id: string; name: string }[];
    service_types: { id: string; name: string }[];
    googler_names: { id: string; name: string }[];
    google_sales_rep_emails: { id: string; name: string }[];
    deal_stage: { id: string; name: string }[];
    sales_rep: { id: string; name: string }[];
  }>({
    companies: [],
    invoice_status: [],
    service_types: [],
    googler_names: [],
    google_sales_rep_emails: [],
    deal_stage: [],
    sales_rep: [],
  });
  const [invoicePreview, setInvoicePreview] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState<[null | Date, null | Date]>([
    null,
    null,
  ]);
  const [filter, setFilter] = useState<{
    company_hs_ids: string[];
    status: string[];
    service_type: string[];
    googler_name: string[];
    google_sales_rep_emails: string[];
    deal_stage: string[];
  }>({
    company_hs_ids: [],
    status: [],
    service_type: [],
    googler_name: [],
    google_sales_rep_emails: [],
    deal_stage: [],
  });
  const [pager, setPager] = useState(initialValue);

  const sortRef = useRef<string>(`sort[invoice_date]=desc`);

  const fetchOptions = async () => {
    setFiltersLoading(true);
    const response: AxiosResponse<FilterOptions> = await axios.get(
      `${process.env.REACT_APP_REPORT_SERVICE}/deal/search/filter`
    );

    setOptions({
      sales_rep: response?.data?.sales_rep || [],
      companies: response?.data?.company || [],
      invoice_status: response?.data?.invoice_status || [],
      service_types: response?.data?.service_type || [],
      deal_stage: response?.data?.deal_stage || [],
      googler_names:
        response?.data?.googler_names.map((item) => ({
          id: item,
          name: item,
        })) || [],
      google_sales_rep_emails:
        response?.data?.google_sales_rep.map((item) => ({
          id: item.id,
          name: item.id,
        })) || [],
    });

    setFiltersLoading(false);
  };

  const pipelineData = useMemo(() => {
    const result = transformAndGroupDataByMonth(data?.revenue_pipeline || []);
    return result || [];
  }, [data?.revenue_pipeline]);

  const handleChange = useCallback(
    (key: string, value: string[]) => {
      setFilter((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [setFilter]
  );

  const handleClosePreview = useCallback(() => {
    setInvoicePreview(null);
    document.body.setAttribute('id', '');
  }, []);

  useEffect(() => {
    fetchPageData({ page: 1, limit: pager.limit || 20 }, sortRef.current);
  }, [filter, dateRange]);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchPageData = async (
    pagination: { page: number; limit: number },
    sort?: string
  ) => {
    try {
      sortRef.current = sort || '';
      setLoading(true);
      setPager(pagination);
      const response: AxiosResponse<IDataState> = await axios.get(
        `${process.env.REACT_APP_REPORT_SERVICE}/deal/search?${sort || ''}`,
        {
          params: {
            ...prepareParams(),
            ...(dateRange && dateRange[0] && dateRange[1]
              ? {
                  invoice_date_from: dayjs(dayjs(dateRange[0])).format(
                    'YYYY-MM-DD'
                  ),
                  invoice_date_to: dayjs(dayjs(dateRange[1])).format(
                    'YYYY-MM-DD'
                  ),
                }
              : {}),
            // https://sprints.zoho.com/workspace/zazmic#P71/itemdetails/I2830
            load_graph_data: true,
            page: pagination.page || 1,
            per_page: pagination.limit || 20,
          },
        }
      );

      const returnData = response?.data || initialState;

      setData(returnData);
      return returnData;
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
      setData(initialState);
      return initialState;
    } finally {
      setLoading(false);
    }
  };

  function prepareParams() {
    return {
      ...Object.entries(filter || {}).reduce((acc, [key, value]) => {
        acc[key as string] = value.join(',');
        return acc;
      }, {} as Record<string, string>),
      // Override invoice status filter:
      // status: getInvoiceState(),
      // If this component used in specific company page:
      // ...(hsId ? { company_hs_ids: hsId } : {}),
    };
  }

  return (
    <StyledPaper
      sx={{
        // FIXME: controls rendering of InvoiceStatusSelect dropdown
        position: 'relative',
        // '.invoice-status-select .MuiPaper-root': {
        //   top: '45px !important',
        //   left: `${isMultiFilterFilled ? 110 : 88}px !important`,
        // },
      }}
    >
      <StyledFilterWrapper $multipleRow={user?.userType !== SALES_REP}>
        <Stack direction="row" gap={3} flexWrap="wrap" py="20px">
          <ButtonDateRangePicker
            inputWidth={140}
            tooltip="Filter by date"
            onChange={setDateRange}
          />

          <SelectWithCheckbox
            loading={filtersLoading}
            filterKey="company_hs_ids"
            label="Company"
            options={options.companies}
            onChange={handleChange}
          />
          {user?.userType && user?.userType !== SALES_REP && (
            <SelectWithCheckbox
              loading={filtersLoading}
              filterKey="googler_name"
              label="Googler Name"
              options={options.googler_names}
              onChange={handleChange}
            />
          )}
          {user?.userType && user?.userType !== SALES_REP && (
            <SelectWithCheckbox
              loading={filtersLoading}
              filterKey="google_sales_rep_emails"
              label="Googler Email"
              options={options.google_sales_rep_emails}
              onChange={handleChange}
            />
          )}
          {user?.userType && user?.userType !== SALES_REP && (
            <SelectWithCheckbox
              loading={filtersLoading}
              filterKey="sales_rep"
              label="Sales Rep"
              options={options.sales_rep}
              onChange={handleChange}
            />
          )}
          {user?.userType && user?.userType !== SALES_REP && (
            <SelectWithCheckbox
              loading={filtersLoading}
              filterKey="google_sales_rep_emails"
              label="Googler Email"
              options={options.google_sales_rep_emails}
              onChange={handleChange}
            />
          )}
          <SelectWithCheckbox
            loading={filtersLoading}
            filterKey="service_type"
            label="Services Type"
            options={options.service_types}
            onChange={handleChange}
          />
          <SelectWithCheckbox
            loading={filtersLoading}
            filterKey="status"
            label="Invoice Status"
            options={options.invoice_status}
            onChange={handleChange}
          />
          <SelectWithCheckbox
            loading={filtersLoading}
            filterKey="deal_stage"
            label="Deal Stage"
            options={options.deal_stage}
            onChange={handleChange}
          />
        </Stack>

        <InvoiceDetails
          invoice_total={data.invoice_total}
          paid_invoice_total={data.paid_invoice_total}
          currency="USD"
        />
      </StyledFilterWrapper>

      <Box sx={{ p: '10px 10px' }}>
        <DealInvoiceChart2
          items={pipelineData || []}
          // TODO: need to update invoiceState and serviceType
          invoiceState={undefined}
          serviceTypes={[
            'gcp_rev',
            'psfdaf_rev',
            'gcp_pipeline',
            'psfdaf_pipeline',
          ]}
          currency="USD"
        />
      </Box>

      {/* <GoogleSalesReportTable
        isForecast={
          filter?.status?.includes('forecast') || filter.status.length === 0
        }
        loading={loading}
        data={data}
        onChange={fetchPageData}
      /> */}
      <SalesReportTable
        isForecast={
          filter?.status?.includes('forecast') || filter.status.length === 0
        }
        loading={loading}
        data={data}
        onInvoiceClick={setInvoicePreview}
        onChange={fetchPageData}
      />

      {invoicePreview && (
        <InvoicePreview id={invoicePreview} onClose={handleClosePreview} />
      )}
    </StyledPaper>
  );
};

export default SalesReport;
